import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionData,
  type Column,
  type ColumnRenderer,
  DateRenderer,
  type ExplorerDataProvider,
  FilterTypes,
  ImgElement,
  NavigationExplorer,
  createDateRangeFilterValidators,
  filterToPostGraphileFilter,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { type ReactNode } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  DeleteDeploymentManifestDocument,
  type DeleteDeploymentManifestMutation,
  type DeleteDeploymentManifestMutationVariables,
  DeploymentManifestsDocument,
  type DeploymentManifestsQuery,
  type DeploymentManifestsQueryVariables,
  type ServiceDeploymentManifestFilter,
  ServiceDeploymentManifestsOrderBy,
} from '../../../../generated/hosting-service';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { getDeploymentStateIconLabel } from '../common/utils';
import classes from './DeploymentManifests.module.scss';

export type ServiceDeploymentManifest = NonNullable<
  DeploymentManifestsQuery['filtered']
>['nodes'][number];

const deploymentStatusRenderer: ColumnRenderer<ServiceDeploymentManifest> = (
  _value,
  data,
): ReactNode => {
  const { label, icon } = getDeploymentStateIconLabel(data.currentlyInUse);
  return (
    <div className={classes.status_container}>
      <ImgElement
        src={icon}
        style={{ width: '20px', height: '20px' }}
        decorative={true}
      />
      <div className={classes.status}>{label}</div>
    </div>
  );
};

const explorerColumns: Column<ServiceDeploymentManifest>[] = [
  {
    label: 'Name',
    propertyName: 'name',
    size: '1fr',
    sortable: true,
  },
  {
    label: 'Deployment State',
    propertyName: 'currentlyInUse',
    size: '1fr',
    sortable: true,
    render: deploymentStatusRenderer,
  },
  {
    label: 'Created At',
    propertyName: 'createdDate',
    size: '1fr',
    sortable: true,
    render: DateRenderer,
  },
];

export const ServiceDeploymentManifests: React.FC = () => {
  const history = useHistory();

  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const generateInlineMenuActions: (
    data: ServiceDeploymentManifest,
  ) => ActionData[] = ({ id }) => {
    const envApolloClient = environmentApolloClient(
      envId,
      new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
    );

    const inlineActions: ActionData[] = [
      {
        label: 'Open Details',
        path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployment-manifests/${id}/configuration`,
      },
      {
        label: 'Delete',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: 'The action is irreversible. By confirming the action the current service deployment manifests is going to be deleted.',
        },

        onActionSelected: async () => {
          await envApolloClient.mutate<
            DeleteDeploymentManifestMutation,
            DeleteDeploymentManifestMutationVariables
          >({
            mutation: DeleteDeploymentManifestDocument,
            variables: {
              input: {
                id: id,
              },
            },
            fetchPolicy: 'no-cache',
          });
          history.replace(history.location.pathname);
        },
      },
    ];
    return inlineActions;
  };

  const dataProvider: ExplorerDataProvider<ServiceDeploymentManifest> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      const result = await envApolloClient.query<
        DeploymentManifestsQuery,
        DeploymentManifestsQueryVariables
      >({
        query: DeploymentManifestsDocument,
        variables: {
          condition: {
            serviceDefinitionId: customizableServiceId,
          },
          filter: filterToPostGraphileFilter<ServiceDeploymentManifestFilter>(
            filters,
            {
              name: 'includesInsensitive',
              currentlyInUse: 'in',
              createdDate: transformRange,
            },
          ),
          orderBy: sortToPostGraphileOrderBy(
            sorting,
            ServiceDeploymentManifestsOrderBy,
          ),
          after: pagingInformation,
        },
        fetchPolicy: 'no-cache',
      });

      return {
        data: result.data.filtered?.nodes ?? [],
        totalCount: result.data.nonFiltered?.totalCount as number,
        filteredCount: result.data.filtered?.totalCount as number,
        hasMoreData: result.data.filtered?.pageInfo.hasNextPage || false,
        pagingInformation: result.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<ServiceDeploymentManifest>();

  return (
    <NavigationExplorer<ServiceDeploymentManifest>
      title="Deployment Manifests"
      keyProperty="id"
      stationKey="ConfigurationManagementExplorer"
      columns={explorerColumns}
      dataProvider={dataProvider}
      calculateNavigateUrl={(item) =>
        `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployment-manifests/${item.id}`
      }
      inlineMenuActions={generateInlineMenuActions}
      filterOptions={[
        {
          label: 'Name',
          property: 'name',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Deployment State',
          property: 'currentlyInUse',
          type: FilterTypes.Options,
          options: [
            {
              label: 'Deployed',
              value: true,
            },
            {
              label: 'Not Deployed',
              value: false,
            },
          ],
        },
        {
          label: 'Creation Period (From)',
          property: 'createdDate',
          type: FilterTypes.Date,
          onValidate: createFromDateFilterValidator('createdDate'),
        },
        {
          label: 'Creation Period (To)',
          property: 'createdDate',
          type: FilterTypes.Date,
          onValidate: createToDateFilterValidator('createdDate'),
        },
      ]}
    />
  );
};

export const ServiceDeploymentManifestsCrumb: BreadcrumbResolver = () =>
  'Deployment Manifests';
